import type { Logger } from '../types/logger';
import { decodeRBSP, inspectSliceHeader, parseSequenceparameterSet, seq_parameter_set_rbsp, slice_layer_without_partitioning_rbsp_idr, slice_layer_without_partitioning_rbsp_non_idr, type H264FrameInfo } from './h264';
import { parseNalUnitsWithLengthPrefix } from './nalus';

/**
 * This function should be invoked on an encoded h264 frame for which
 * both frame_mbs_only_flag and mb_adaptive_frame_field_flag are false,
 * i.e. we don't know if it is progressive or interlaced. It will inspect
 * NAL units to figure which one it is.
 *
 * It is important to invoke it only on frames from mp4 and not mpegts, since
 * mp4 frames are using a prefix length encoding which is diffrerent from the
 * emulation prevention encoding used in mpegts.
 */
export function isProgressiveH264FrameFromMp4(
  logger: Logger,
  frameInfo: H264FrameInfo,
  nalLengthSize: number,
  encodedH264Frame: Uint8Array,
): boolean {
  if (frameInfo.frame_mbs_only_flag) {
    throw new Error('Should not invoke isProgressiveH264FrameFromMp4 when frame_mbs_only_flag is true');
  }
  if (frameInfo.mb_adaptive_frame_field_flag) {
    throw new Error('Should not invoke isProgressiveH264FrameFromMp4 when mb_adaptive_frame_field_flag is true');
  }
  const nalus = parseNalUnitsWithLengthPrefix(nalLengthSize, encodedH264Frame);
  for (const nalu of nalus) {
    if (nalu.naluCode === seq_parameter_set_rbsp) {
      logger.warn('Found SPS NAL unit in first frame (should not happen in mp4)');
      const rbsp = decodeRBSP(nalu.data);
      const info = parseSequenceparameterSet(rbsp);
      if (info.frame_mbs_only_flag) {
        logger.warn('The frame\'s SPS NAL unit has frame_mbs_only_flag set to true, which contradicts the avcC data');
        return true;
      }

      if (info.mb_adaptive_frame_field_flag) {
        logger.warn('The frame\'s SPS NAL unit has mb_adaptive_frame_field_flag set to true, which contradicts the avcC data');
        return false;
      }

      frameInfo = info;
      continue;
    }

    if (nalu.naluCode === slice_layer_without_partitioning_rbsp_non_idr || nalu.naluCode === slice_layer_without_partitioning_rbsp_idr) {
      const rbsp = decodeRBSP(nalu.data);
      const sliceHeaderInfo = inspectSliceHeader(rbsp, frameInfo);
      return sliceHeaderInfo.field_pic_flag !== true;
    }
  }
  throw new Error('Could not find information about progressive/interlaced in NAL units');
}


